import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Switch,
  Chip,
  Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Add as AddIcon,
} from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  headerCell: {
    fontWeight: 'bold',
    backgroundColor: theme.palette.grey[100],
  },
  actionsCell: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  noRules: {
    padding: theme.spacing(4),
    textAlign: 'center',
  },
}));

const RuleListComponent = ({
  rules,
  isLoading,
  onCreateNew,
  onEdit,
  onDelete,
  onToggleActive,
  onGenerateNow,
  allEnums,
  isReadOnly = false,
  triggerServices,
}) => {
  const classes = useStyles();

  if (isLoading) {
    return (
      <Box className={classes.container}>
        <Typography>Loading rules...</Typography>
      </Box>
    );
  }

  const taskTypes =
    allEnums?.taskTypes && allEnums.taskTypes.length > 0
      ? allEnums.taskTypes.filter(s => s.isActive === true)
      : [{ id: '00000000-0000-0000-0000-000000000000', title: '' }];

  // Resolve against every catalog row (inactive included) so a deactivated group's name still shows
  const getServiceName = (triggerServiceId) =>
    triggerServices.find(s => s.taskTriggerServiceGroupId === triggerServiceId)?.name ?? 'Unknown Service';

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h4">Task Rules</Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={onCreateNew} disabled={isReadOnly}>
          Create New Rule
        </Button>
      </Box>

      {rules.length === 0 ? (
        <Paper className={classes.noRules}>
          <Typography variant="h6" color="textSecondary">
            No task rules created yet
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            Click "Create New Rule" to get started
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}>Rule Name</TableCell>
                <TableCell className={classes.headerCell}>Service</TableCell>
                <TableCell className={classes.headerCell}>Level</TableCell>
                <TableCell className={classes.headerCell}>Task Type</TableCell>
                <TableCell className={classes.headerCell}>Active</TableCell>
                <TableCell className={classes.headerCell}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map((rule) => (
                <TableRow key={rule.taskRuleId}>
                  <TableCell>
                    <Box>
                      <Typography variant="body1">{rule.ruleName}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {rule.description}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {getServiceName(rule.triggerServiceId)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={rule.isSegmentLevel ? 'Segment' : 'File'}
                      size="small"
                      color={rule.isSegmentLevel ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {taskTypes && taskTypes.length > 0 ? taskTypes.find(t => t.id === rule.taskTypeId)?.title : 'Unknown Task Type'}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={rule.isActive}
                      onChange={() => onToggleActive(rule)}
                      color="primary"
                      disabled={isReadOnly}
                    />
                  </TableCell>
                  <TableCell>
                    <Box className={classes.actionsCell}>
                      <Tooltip title="Edit Rule">
                        <IconButton size="small" onClick={() => onEdit(rule.taskRuleId)} disabled={isReadOnly}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Generate Tasks Now">
                        <IconButton size="small" onClick={() => onGenerateNow(rule.taskRuleId)} disabled={isReadOnly}>
                          <PlayIcon fontSize="small" color="primary" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Delete Rule">
                        <IconButton size="small" onClick={() => onDelete(rule.taskRuleId)} disabled={isReadOnly}>
                          <DeleteIcon fontSize="small" color="secondary" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

RuleListComponent.propTypes = {
  rules: PropTypes.arrayOf(
    PropTypes.shape({
      taskRuleId: PropTypes.number.isRequired,
      ruleName: PropTypes.string.isRequired,
      description: PropTypes.string,
      triggerServiceId: PropTypes.string,
      isSegmentLevel: PropTypes.bool.isRequired,
      taskTypeId: PropTypes.string,
      isActive: PropTypes.bool.isRequired,
    })
  ).isRequired,
  isLoading: PropTypes.bool,
  allEnums: PropTypes.object,
  triggerServices: PropTypes.arrayOf(
    PropTypes.shape({
      taskTriggerServiceGroupId: PropTypes.string,
      name: PropTypes.string,
      sortOrder: PropTypes.number,
      isActive: PropTypes.bool,
    }),
  ),
  onCreateNew: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onToggleActive: PropTypes.func.isRequired,
  onGenerateNow: PropTypes.func.isRequired,
};

RuleListComponent.defaultProps = {
  isLoading: false,
  allEnums: {},
  triggerServices: [],
};

export default RuleListComponent;
